'use client';

import { EventClickElement } from '@tuic/lib/tracking/tealium';
import { useEffect } from 'react';
import { twMerge } from 'tailwind-merge';

import { Icon, IconButton } from '../../../core-components';
import { expandStringWithFallback, type I18NInterface } from '../../../utils';
import { UniversalCarousel } from '../UniversalCarousel';
import { CarouselStage } from '../UniversalCarousel';
import { CarouselInterface } from '../UniversalCarousel';

import type { MouseEventHandler, PropsWithChildren} from 'react';

type I18N = I18NInterface<'pagination'>;

export type MediaCarouselProps = PropsWithChildren<{
  i18n?: I18N;
  initialIndex?: number;
  onIndexChange?: (index: number) => void;
  viewMode?: 'basic' | 'withIndexTracking';
  className?: string;
}>;

const CarouselControls = ({
  prev,
  next,
  visibleIndexes,
  onIndexChange,
}: {
  prev: () => void;
  next: () => void;
  visibleIndexes: number[];
  onIndexChange: (index: number) => void;
}) => {
  useEffect(() => {
    onIndexChange(visibleIndexes[0]);
  }, [visibleIndexes, onIndexChange]);

  return <CarouselButtons prev={prev} next={next} />;
};

const CarouselButtons = ({
  prev,
  next,
}: {
  prev: () => void;
  next: () => void;
}) => {
  const handlePrevClick: MouseEventHandler = ev => {
    ev.preventDefault();
    prev();
  };

  const handleNextClick: MouseEventHandler = ev => {
    ev.preventDefault();
    next();
  };

  return (
    <>
      <IconButton
        className="absolute group-hover:opacity-100 opacity-100 md:opacity-0 transition-opacity left-2 top-1/2 -translate-y-1/2"
        data-track-click-label="media-carousel-prev"
        data-track-click-element={EventClickElement.Button}
        aria-label="media-carousel-prev"
        iconName="nav-arrow-left"
        size="sm"
        onClick={handlePrevClick}
        variant="primary"
        on="image"
      />
      <IconButton
        className="absolute group-hover:opacity-100 opacity-100 md:opacity-0 transition-opacity right-2 top-1/2 -translate-y-1/2"
        data-track-click-label="media-carousel-next"
        data-track-click-element={EventClickElement.Button}
        aria-label="media-carousel-next"
        iconName="nav-arrow-right"
        size="sm"
        onClick={handleNextClick}
        variant="primary"
        on="image"
      />
    </>
  );
};

export const MediaCarousel = (props: MediaCarouselProps) => {
  const {
    children,
    i18n,
    initialIndex = 0,
    onIndexChange,
    viewMode = 'basic',
    className,
  } = props;

  return (
    <div className={twMerge('relative h-full group', className)}>
      <UniversalCarousel initialIndex={initialIndex}>
        <CarouselStage className="h-full carousel-stage-gap-2 md:carousel-stage-gap-6">
          {children}
        </CarouselStage>
        <CarouselInterface>
          {context => {
            const { prev, next, visibleIndexes } = context;

            if (viewMode === 'withIndexTracking' && onIndexChange) {
              return (
                <CarouselControls
                  prev={prev}
                  next={next}
                  visibleIndexes={visibleIndexes}
                  onIndexChange={onIndexChange}
                />
              );
            }
            return <CarouselButtons prev={prev} next={next} />;
          }}
        </CarouselInterface>
        <CarouselInterface>
          {context => {
            const { visibleIndexes, lastIndex } = context;

            const message = expandStringWithFallback(
              i18n?.pagination,
              '{{ count }} / {{ total }}',
              {
                count: visibleIndexes[0] + 1,
                total: lastIndex + 1,
              },
            );

            return (
              <div className="absolute bottom-2 right-2 flex items-center justify-center gap-1 rounded-md bg-black/60 py-1 pl-2 pr-4 text-center text-sm text-white">
                <Icon name="media-image-list" size="sm" />
                <span>{message}</span>
              </div>
            );
          }}
        </CarouselInterface>
      </UniversalCarousel>
    </div>
  );
};

MediaCarousel.displayName = 'MediaCarousel';
